import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useId, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import CountryFlag from "@entrilia/shared/components/CountryFlag";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { getUserLocaleCode, getUserLocaleInfo, supportedLocales } from "@entrilia/shared/utilities/userLocaleConfig";
import { nextNextFrame } from "@entrilia/shared/utilities/windowHelper";
import adminApi from "../../../api/adminApi";
import ExampleFormattingTable from "./ExampleFormattingTable";

const updateUserPreferences = withErrorHandling(adminApi.updateUserPreferences);

const RegionalFormatSection = () => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const selectLabelId = useId();

  const [selectedLanguageTag, setSelectedLanguageTag] = useState(getUserLocaleCode);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const [, error] = await updateUserPreferences({
      languageTag: selectedLanguageTag,
    });
    setSaving(false);

    if (error) {
      logError(error, "[RegionalFormatSection] updateUserPreferences");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Settings updated successfully");

    nextNextFrame(() => {
      window.location.reload();
    });
  };

  const handleCancel = () => {
    setSelectedLanguageTag(getUserLocaleCode());
  };

  const selectedLocale = supportedLocales.find((l) => l.locale.code === selectedLanguageTag) ?? getUserLocaleInfo();

  const isFormDirty = selectedLanguageTag !== getUserLocaleCode();

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5} sx={{ py: 2 }}>
        <Typography variant="subtitle1">Regional Format</Typography>
        <Typography variant="caption" color="textSecondary">
          Select the regional style for how dates, time, numbers, and currencies appear in the system.
        </Typography>
      </Stack>
      <FormControl fullWidth>
        <InputLabel id={selectLabelId}>Preferred Region</InputLabel>
        <Select
          label="Preferred Region"
          labelId={selectLabelId}
          value={selectedLanguageTag}
          onChange={(e) => setSelectedLanguageTag(e.target.value)}
        >
          {supportedLocales.map(({ locale, displayName, countryCode }) => (
            <MenuItem key={locale.code} value={locale.code}>
              <Stack direction="row" spacing={1}>
                <CountryFlag countryCodeAlpha2={countryCode} />
                <Typography>{displayName}</Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ExampleFormattingTable selectedLocale={selectedLocale} />
      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={handleSave} loading={saving} disabled={!isFormDirty}>
          Save
        </Button>
        <Button variant="text" color="secondary" onClick={handleCancel} disabled={!isFormDirty}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default RegionalFormatSection;
